import styled from '@emotion/styled';
import React, { FC, useEffect, useState } from 'react';
import { ImpulseSpinner } from '~Components/ImpulseSpinner';
import { LoyaltyMember, LoyaltySearchState, useButton, useDebounce } from '~Hooks';
import useI18n from '~Translations';
import { Input, SearchInputWrapper } from '~Styles';
import {
  Body,
  C,
  CancelButton,
  CloseButton,
  EmptyState,
  ErrorNote,
  Footer,
  SpinnerRow,
  TRANSITION,
} from '../styles';

/** Characters before the search fires. One is a keystroke, two is a query. */
const SEARCH_MIN_CHARS = 2;

const SearchField = styled(SearchInputWrapper)`
  margin-top: 18px;
`;

const ResultRow = styled.button<{ touching?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
  margin-top: 10px;
  padding: 16px;
  text-align: left;
  cursor: pointer;
  border-radius: 12px;
  background: ${({ touching }) => (touching ? C.surfacePressed : C.surfaceInset)};
  border: 1px solid ${({ touching }) => (touching ? C.textMuted : C.hairline)};
  transition: background ${TRANSITION}, border-color ${TRANSITION};

  &:focus-visible {
    outline: 2px solid ${C.accent};
    outline-offset: 2px;
  }
`;

const ResultName = styled.span`
  display: block;
  color: ${C.textPrimary};
  font-size: 15px;
  font-weight: 600;
`;

const ResultDetail = styled.span`
  display: block;
  margin-top: 3px;
  color: ${C.textSecondary};
  font-size: 12px;
`;

const ResultLevel = styled.span`
  flex: none;
  color: ${C.accent};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
`;

/**
 * Its own component so each row can have its own useButton - hooks can't be
 * called inside the results map.
 */
const SearchResult: FC<{
  member: LoyaltyMember;
  onSelect: (member: LoyaltyMember) => void;
}> = ({ member, onSelect }) => {
  const { i18n } = useI18n();
  const button = useButton({ onClick: () => onSelect(member) });

  return (
    <ResultRow type="button" {...button}>
      <span>
        <ResultName>{member.name || member.email || i18n('Loyalty.UnnamedMember')}</ResultName>
        {member.name && member.email && <ResultDetail>{member.email}</ResultDetail>}
      </span>
      {member.currentMembershipLevel && (
        <ResultLevel>{i18n(member.currentMembershipLevel.name)}</ResultLevel>
      )}
    </ResultRow>
  );
};

interface Props {
  /** A tapped card matched nobody and is still in hand while staff search. */
  heldCard: boolean;
  searchState: LoyaltySearchState;
  onSearch: (term: string) => void;
  onSelect: (member: LoyaltyMember) => void;
  onBack: () => void;
  onKeepCard: () => void;
}

/**
 * Free-text member lookup. The one input matches whatever staff have to go on:
 * a name, an email, a personnummer.
 */
const SearchStep: FC<Props> = ({
  heldCard,
  searchState,
  onSearch,
  onSelect,
  onBack,
  onKeepCard,
}) => {
  const { i18n } = useI18n();
  const [term, setTerm] = useState('');
  const debouncedTerm = useDebounce(term, 300);
  const isTermSearchable = debouncedTerm.trim().length >= SEARCH_MIN_CHARS;

  const backButton = useButton({ onClick: onBack });
  const keepCardButton = useButton({ onClick: onKeepCard });

  useEffect(() => {
    if (isTermSearchable) {
      onSearch(debouncedTerm.trim());
    }
  }, [debouncedTerm]);

  // The parent's result state can outlive a search session; only the results
  // of the term on screen are ever shown.
  const results = isTermSearchable ? searchState.results : null;

  return (
    <>
      <Body>
        <SearchField fluid value={term} onClear={() => setTerm('')}>
          <Input
            value={term}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTerm(event.target.value)}
            placeholder={i18n('Loyalty.SearchPlaceholder')}
            dark
            isSearch
            fluid
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        </SearchField>

        {searchState.loading && (
          <SpinnerRow>
            <ImpulseSpinner size={32} />
          </SpinnerRow>
        )}

        {!searchState.loading &&
          results?.map((member) => (
            <SearchResult key={member.id} member={member} onSelect={onSelect} />
          ))}

        {!searchState.loading && results && results.length === 0 && (
          <EmptyState>{i18n('Loyalty.NoResults')}</EmptyState>
        )}

        {searchState.error && <ErrorNote>{searchState.error}</ErrorNote>}
      </Body>

      <Footer>
        <CancelButton type="button" {...backButton}>
          {i18n('Loyalty.Back')}
        </CancelButton>
        {/* The tapped card is still in hand; searching does not spend it. */}
        {heldCard ? (
          <CloseButton type="button" {...keepCardButton}>
            {i18n('Loyalty.KeepCard')}
          </CloseButton>
        ) : (
          <span />
        )}
      </Footer>
    </>
  );
};

export default SearchStep;
